import { Menu, CircularProgress } from "@mui/material";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import { saveAs } from "file-saver";
import { toast } from "react-toastify";
import { fetchLocalCommunityAds } from "../Data";
import { htmlToText } from "html-to-text"; // Import html-to-text

const DownloadAds = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isLoading, setIsLoading] = useState(false); // Loading state

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  // Get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    return new Date().toISOString().split("T")[0];
  };

  // Validate date range (max 3 months)
  const isValidDateRange = (start, end) => {
    if (!start || !end) return true; // Allow empty dates for validation
    const startDateObj = new Date(start);
    const endDateObj = new Date(end);
    const threeMonthsInMs = 3 * 30 * 24 * 60 * 60 * 1000; // Approx 3 months in milliseconds
    return (
      endDateObj - startDateObj <= threeMonthsInMs && endDateObj >= startDateObj
    );
  };

  // Function to strip HTML tags from a string
  const stripHtml = (html) => {
    if (!html) return html;
    return htmlToText(html, {
      wordwrap: false,
      preserveNewlines: true,
      tags: {
        div: { format: "block" },
        br: { format: "inline", options: { noTrailingWhitespace: true } },
      },
    }).trim();
  };

  // Alternative regex-based approach (if avoiding dependencies)
  // const stripHtml = (html) => {
  //   if (!html) return html;
  //   return html.replace(/<[^>]+>/g, "").replace(/\s+/g, " ").trim();
  // };

  const handleDownload = async () => {
    if (!startDate || !endDate) {
      toast.error("Please select both start and end dates.");
      return;
    }

    if (!isValidDateRange(startDate, endDate)) {
      toast.error(
        "Date range must be within 3 months and end date must be after start date."
      );
      return;
    }

    setIsLoading(true); // Start loading
    try {
      const filteredData = await fetchLocalCommunityAds({
        fromDate: startDate,
        toDate: endDate,
      });

      if (!filteredData || filteredData.length === 0) {
        toast.info("No data found for the selected date range.");
        return;
      }

      const processedData = filteredData.map((item) => ({
        ...item,
        Description: stripHtml(item.Description), // Strip HTML from Description
        eventDetails: formatArray(item.eventDetails),
        availableFrom: formatArray(item.availableFrom),
      }));

      const csvHeaders = Object.keys(processedData[0]).join(",");
      const csvRows = processedData.map((row) =>
        Object.values(row)
          .map((value) =>
            typeof value === "string" ? `"${value.replace(/"/g, '""')}"` : value
          )
          .join(",")
      );
      const csvContent = [csvHeaders, ...csvRows].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const formattedStartDate = startDate.replace(/-/g, "");
      const formattedEndDate = endDate.replace(/-/g, "");
      const fileName = `ads_${formattedStartDate}_to_${formattedEndDate}.csv`;

      saveAs(blob, fileName);
      toast.success("Download successful!");
      handleClose();
    } catch (error) {
      toast.error("Failed to fetch data. Please try again.");
      console.error("API error:", error);
    } finally {
      setIsLoading(false); // Stop loading
    }
  };

  const formatArray = (arrayString) => {
    try {
      const array = JSON.parse(arrayString);
      if (Array.isArray(array)) {
        return array.map((entry) => JSON.stringify(entry)).join("; ");
      }
      return arrayString;
    } catch (error) {
      return arrayString;
    }
  };

  return (
    <div aria-hidden={true}>
      <span onClick={handleClick}>Download Ads</span>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          style: {
            minWidth: "300px",
            borderRadius: "12px",
            padding: "20px",
            zIndex: 1301,
            // boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
          },
        }}
        BackdropProps={{
          style: {
            backgroundColor: "rgba(0, 0, 0, 0.5)",
          },
        }}
        ModalProps={{
          keepMounted: true,
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "15px" }}>
          <label
            style={{
              display: "flex",
              flexDirection: "column",
              fontSize: "14px",
              fontWeight: "500",
            }}
          >
            Start Date:
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              max={getTodayDate()} // Prevent future dates
              style={{
                padding: "8px",
                borderRadius: "5px",
                border: "1px solid #ccc",
                marginTop: "5px",
              }}
              disabled={isLoading} // Disable input during loading
            />
          </label>
          <label
            style={{
              display: "flex",
              flexDirection: "column",
              fontSize: "14px",
              fontWeight: "500",
            }}
          >
            End Date:
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              max={getTodayDate()} // Prevent future dates
              min={startDate} // Ensure end date is after start date
              style={{
                padding: "8px",
                borderRadius: "5px",
                border: "1px solid #ccc",
                marginTop: "5px",
              }}
              disabled={isLoading} // Disable input during loading
            />
          </label>
          <button
            onClick={handleDownload}
            style={{
              backgroundColor: "#28A745",
              color: "white",
              padding: "10px",
              border: "none",
              borderRadius: "5px",
              fontSize: "16px",
              fontWeight: "bold",
              cursor: isLoading ? "not-allowed" : "pointer",
              textAlign: "center",
              opacity: isLoading ? 0.6 : 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
            }}
            disabled={isLoading} // Disable button during loading
          >
            {isLoading ? (
              <>
                <CircularProgress size={20} color="inherit" />
                Preparing to download ...
              </>
            ) : (
              "Download"
            )}
          </button>
        </div>
        <div
          style={{
            position: "absolute",
            top: "10px",
            right: "10px",
            cursor: isLoading ? "not-allowed" : "pointer",
            color: "blue",
            opacity: isLoading ? 0.6 : 1,
          }}
          onClick={() => {
            if (!isLoading) {
              setStartDate("");
              setEndDate("");
            }
          }}
        >
          Clear
        </div>
      </Menu>
    </div>
  );
};

export default DownloadAds;
